<template>
  <el-config-provider :locale="locale">
    <router-view></router-view>
    <ErrorModal></ErrorModal>
  </el-config-provider>
</template>
<script lang="ts" setup>
import { onMounted, onUnmounted, ref, watch } from "vue";
import { useStore, AccountType } from "@/store";
import ja from "element-plus/dist/locale/ja.mjs";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { getCookie } from "./utils";
import ErrorModal from "@/common/components/ErrorModal.vue";
import services from "@/api/services";
import { showErrorCondition, showErrorMessage } from "@/utils";
const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

// 環境変数から真偽値を取得
const isMypageLoginEnabled =
  process.env.VUE_APP_IS_MYPAGE_LOGIN_ENABLED === "true";

store.commit("closeLoading");
watch(
  () => store.state.scrollTopStatus,
  () => {
    const container = document.querySelector(".container");
    if (container) {
      container.scrollTo({
        left: 0,
        top: 0,
        behavior: "smooth",
      });
    }
  },
);
watch(
  () => route.path,
  () => {
    const container = document.querySelector(".container");
    if (container) {
      container.scrollTo(0, 0);
    }
  },
);

const setLoadingStatus = () => {
  // remember user setting page status when refresh page
  if (route.path === "/user-setting" || route.path === "/user-setting-audit") {
    store.commit("setUserSettingUnload", route.path);
  }
};
window.onbeforeunload = () => {
  setLoadingStatus();
  store.commit("closeErrorModal");
};

window.onpagehide = () => {
  setLoadingStatus();
  store.commit("closeErrorModal");
};

const resize = () => {
  // define --vh for sp
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty("--vh", `${vh}px`);
  store.commit("setBodyWidth", document.body.clientWidth);
};

window.addEventListener("resize", resize);

onMounted(() => {
  const sessionIDCookieName =
    process.env.VUE_APP_SESSION_ID_COOKIE_NAME ?? "session_id";
  resize();

  if (!getCookie(sessionIDCookieName)) {
    store.commit("setIsLogin", false);
  } else if (isMypageLoginEnabled) {
    // クッキーがある場合、ユーザー情報を取得してログイン処理
    services.dev.devUserinfoGet().then(
      (res) => {
        store.commit("setInitPassword", undefined);
        store.commit("setUserinfo", res.data);
        store.commit("setIsLogin", true);
        store.commit("setLoginUsername", res.data.userinfo?.mail_address);

        store.commit(
          "setIsDedicatedOptionEnabledDeveloper",
          res.data.user_developer?.dedicated_option_enabled ||
            res.data.corporation_developer?.dedicated_option_enabled,
        );
        const currentAccountType = store.state.currentAccountType;
        if (
          currentAccountType === AccountType.user &&
          res.data.user_developer
        ) {
          store.commit("setDeveloperId", res.data.user_developer.developer_id);
          store.commit(
            "setIsDedicatedOptionEnabledDeveloper",
            res.data.user_developer.dedicated_option_enabled,
          );
        } else if (
          currentAccountType === AccountType.corporation &&
          res.data.corporation_developer
        ) {
          store.commit(
            "setDeveloperId",
            res.data.corporation_developer?.developer_id,
          );
          store.commit(
            "setIsDedicatedOptionEnabledDeveloper",
            res.data.corporation_developer.dedicated_option_enabled,
          );
        } else {
          if (res.data.user_developer) {
            store.commit("setCurrentAccountType", AccountType.user);
            store.commit(
              "setDeveloperId",
              res.data.user_developer.developer_id,
            );
          } else if (res.data.corporation_developer) {
            store.commit("setCurrentAccountType", AccountType.corporation);
            store.commit(
              "setDeveloperId",
              res.data.corporation_developer?.developer_id,
            );
          }
        }
        if (!res.data.user_developer && !res.data.corporation_developer) {
          store.commit("setDeveloperId", undefined);
          router.replace({
            path: "/user-setting",
          });
        }
      },
      (reason) => {
        // エラー時の処理
        if (reason.response?.status === 428) {
          // TODO: ログイン導線のMypage移行に伴い、規約未同意（428エラー）時の挙動を修正予定。
          // 将来的には自前の /password-set への遷移を廃止し、Cookieを削除した上で
          // Mypageのログイン画面（https://st.safie.link/mypage/auth/login...）へリダイレクトさせる。

          // パスワード設定ページへのリダイレクトが必要な場合
          router.push("/password-set");
        } else if (showErrorCondition(reason)) {
          showErrorMessage(reason.response.data.message);
        }
        // エラーの場合はログアウト状態にする
        store.commit("setIsLogin", false);
      },
    );
  }
});
onUnmounted(() => {
  window.removeEventListener("resize", resize);
});
// locale
const locale = ref(ja);
locale.value.el = {
  ...ja.el,
  // pagination
  pagination: {
    ...ja.el.pagination,
    goto: t("elementPlus.PAGINATION_GOTO_TEXT"),
    pagesize: t("elementPlus.PAGINATION_PAGE_SIZE_TEXT"),
    total:
      t("elementPlus.PAGINATION_TOTAL_PRE_TEXT") +
      "{total}" +
      t("elementPlus.PAGINATION_TOTAL_SUF_TEXT"),
    pageClassifier: t("elementPlus.PAGINATION_PAGE_CLASSIFIER_TEXT"),
  },
  // table
  table: {
    ...ja.el.table,
    emptyText: t("elementPlus.TABLE_EMPTY_TEXT"),
  },
  colorpicker: {
    ...ja.el.colorpicker,
    confirm: t("elementPlus.COLOR_PICKER_CONFIRM"),
    clear: t("elementPlus.COLOR_PICKER_CLEAR"),
  },
  select: {
    ...ja.el.select,
    placeholder: "",
  },
};
</script>
<style lang="scss" scoped></style>
